import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { supabase, isSupabaseReady } from "../lib/supabaseClient";
import Login from "../components/espace/Login";
import ClientDashboard from "../components/espace/ClientDashboard";
import AdminDashboard from "../components/espace/AdminDashboard";
import Seo from "../components/Seo";

export default function EspaceClient() {
  const [session, setSession] = useState(undefined); // undefined = en cours
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (!isSupabaseReady) { setSession(null); return; }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) { setProfile(null); return; }
    setProfileLoading(true);
    supabase.from("profiles").select("*").eq("id", session.user.id).single()
      .then(({ data }) => { setProfile(data); setProfileLoading(false); });
  }, [session]);

  const signOut = async () => { await supabase.auth.signOut(); setProfile(null); };

  let body;
  if (!isSupabaseReady) {
    body = (
      <div className="bento p-8 max-w-lg mx-auto text-center">
        <h2 className="font-display text-2xl tracking-tight mb-3">Espace client — configuration</h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Les clés Supabase ne sont pas encore chargées. Vérifiez le fichier <code>.env</code> puis relancez le site.
        </p>
      </div>
    );
  } else if (session === undefined) {
    body = <p className="text-center text-zinc-500">Chargement…</p>;
  } else if (!session) {
    body = <Login />;
  } else if (profileLoading || !profile) {
    body = <p className="text-center text-zinc-500">Chargement de votre espace…</p>;
  } else {
    body = (
      <div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              {profile.role === "admin" ? "Console admin" : "Votre espace"}
            </span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tighter leading-[0.9] mt-2">
              {profile.role === "admin" ? "Vos projets clients" : "Vos projets"}
            </h1>
          </div>
          <button onClick={signOut} className="btn-pill text-sm">
            <LogOut className="w-4 h-4" /> {profile.email}
          </button>
        </div>
        {profile.role === "admin" ? <AdminDashboard profile={profile} /> : <ClientDashboard profile={profile} />}
      </div>
    );
  }

  return (
    <>
      <Seo title="Espace client" description="Accès sécurisé à votre espace client ML. Agence Web : suivi de projet, fichiers et messagerie." noindex />
      <Navigation />
      <main data-testid="espace-client-page" className="relative z-10 min-h-screen px-5 sm:px-8 lg:px-12 pt-36 sm:pt-44 pb-28">
        <div className="w-full">{body}</div>
      </main>
      <Footer />
    </>
  );
}
